import React, { useMemo, useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  AcademicCapIcon, 
  UserGroupIcon, 
  PlayIcon, 
  UserIcon, 
  ShoppingCartIcon,
  StarIcon,
  MagnifyingGlassIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { Course } from '../types';
import { shouldShowIOSWebViewAlert } from '../utils/deviceDetection';
import { useUsers } from '../hooks/useUsers';
import UserProfileLink from './UserProfileLink';

interface FeaturedCourseCardProps {
  course: Course;
  index: number;
  currentUser: any;
  addToCart: (item: any) => void;
}

const FeaturedCourseCard: React.FC<FeaturedCourseCardProps> = React.memo(({ 
  course, 
  index, 
  currentUser, 
  addToCart 
}) => {
  const { fetchUsers } = useUsers();
  const [showHelperText, setShowHelperText] = useState(false);
  
  // Fetch course author data when course changes
  React.useEffect(() => {
    if (course?.author?.id) {
      fetchUsers([course.author.id]);
    }
  }, [course?.author?.id, fetchUsers]);

  // Memoize expensive computations
  const coursePrice = useMemo(() => {
    return course.price_status === 'free' ? 'FREE' : `₵${course.pricee || '0'}`;
  }, [course.price_status, course.pricee]);

  const courseProgram = useMemo(() => {
    return Array.isArray(course.program) ? course.program[0] : course.program || 'General';
  }, [course.program]);

  const courseDescription = useMemo(() => {
    return course.summary || course.description || 'No description available';
  }, [course.summary, course.description]);

  const [showFullImage, setShowFullImage] = useState(false);

  // Memoize event handlers
  const handleAddToCart = useCallback(() => {
    addToCart({
      type: "course",
      itemId: course.id,
      name: course.name,
      price: parseFloat(course.pricee) || 0,
      image: course.image_url
    });
  }, [addToCart, course]);

  return (
    <div className="group transform hover:scale-[1.02] transition-all duration-300">
      {/* Featured course number badge */}
      <div className="relative">
        <div className="absolute -top-3 -left-3 z-20">
          <div className="bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
            <span className="font-bold text-sm">#{index + 1}</span>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300 overflow-hidden relative">
          {/* Course Image */}
          <div className="relative w-full h-72 bg-gray-100 overflow-hidden">
            {course.image_url ? (
              <img 
                src={course.image_url} 
                alt={course.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <AcademicCapIcon className="h-24 w-24 text-gray-400" />
              </div>
            )}
            
            {/* Featured badge */}
            <div className="absolute top-2 right-2 bg-orange-500 text-white px-2 py-1 rounded text-xs font-bold flex items-center shadow-sm">
              <StarIcon className="h-3 w-3 mr-1 fill-current" />
              FEATURED
            </div>
            
            {/* Rating Badge */}
            {course.rating > 0 && (
              <div className="absolute top-2 left-2 bg-white px-2 py-1 rounded text-gray-800 flex items-center shadow-sm">
                <StarIcon className="h-3 w-3 mr-1 fill-current text-yellow-500" />
                <span className="text-xs font-bold">{course.rating.toFixed(1)}</span>
              </div>
            )}
            
            {/* Program Tag */}
            <div className="absolute bottom-4 right-4">
              <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold bg-white/95 text-slate-700 backdrop-blur-sm shadow-lg border border-slate-200">
                {courseProgram}
              </span>
            </div>

            {/* Price Badge - Hidden on iOS WebView */}
            {!shouldShowIOSWebViewAlert() && (
              <div className="absolute bottom-4 left-4">
                <span className={`px-3 py-1.5 text-sm font-bold rounded-lg shadow-lg border border-white/20 ${
                  course.price_status === "Free" || course.pricee === "0"
                    ? "bg-green-600 text-white"
                    : "bg-slate-900 text-white"
                }`}>
                  {course.price_status === "Free" || course.pricee === "0"
                    ? "FREE"
                    : `₵${course.pricee || '0'}`}
                </span>
              </div>
            )}

            {/* Expand Image Icon */}
            {course.image_url && (
              <button
                onClick={() => setShowFullImage(true)}
                className="absolute top-4 left-4 bg-white/90 hover:bg-white text-gray-700 hover:text-blue-600 p-2 rounded-full shadow-sm transition-all duration-200 opacity-0 group-hover:opacity-100"
                title="View full image"
              >
                <MagnifyingGlassIcon className="h-4 w-4" />
              </button>
            )}
          </div>
          
          {/* Course Content */}
          <div className="p-3">
            {/* Title */}
            <h3 className="text-sm font-bold text-gray-900 mb-1 line-clamp-2 min-h-[2.5rem]">
              {course.name}
            </h3>
            
            {/* Rating Display */}
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center">
                <span className="text-sm font-bold text-gray-900 mr-1">
                  {course.rating > 0 ? course.rating.toFixed(1) : 'No ratings'}
                </span>
                <div className="flex items-center">
                  {[...Array(5)].map((_, index) => (
                    <svg
                      key={index}
                      className={`h-3 w-3 ${
                        course.rating > 0 && index < Math.floor(course.rating)
                          ? 'text-yellow-400'
                          : course.rating > 0 && index < course.rating
                          ? 'text-yellow-400'
                          : 'text-yellow-300'
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
              <span className="text-xs text-gray-500">
                {course.rating > 0 ? `(${(course as any).reviews_count || 0} reviews)` : '(Be the first to rate)'}
              </span>
            </div>
            
            {/* Description */}
            <div className="mb-2">
              <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">
                {courseDescription}
              </p>
            </div>

            {/* Course Stats */}
            <div className="flex items-center gap-3 mb-2 text-xs text-gray-600">
              <span className="flex items-center">
                <svg className="h-3 w-3 mr-1 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                </svg>
                {course.lessons_count || 0} lessons
              </span>
              <span className="flex items-center">
                <svg className="h-3 w-3 mr-1 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {course.duration || '0h'}
              </span>
              {Array.isArray((course as any).classrooms) && (course as any).classrooms.length > 0 && (
                <span className="flex items-center">
                  <svg className="h-3 w-3 mr-1 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                  </svg>
                  {(course as any).classrooms.length} classrooms
                </span>
              )}
            </div>

            {/* Author */}
            <div className="mb-3 text-xs text-gray-600 flex items-center">
              <svg className="h-3 w-3 mr-1 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
              </svg>
              {course.author ? (
                <UserProfileLink 
                  userId={course.author.id}
                  fallbackName={course.author.name}
                  fallbackAvatarUrl={course.author.image_url}
                  className="hover:text-primary-600 transition-colors"
                />
              ) : (
                'Unknown Author'
              )}
            </div>
            
            {/* Action Button - Hidden on iOS WebView for paid courses */}
            {!shouldShowIOSWebViewAlert() || course.price_status === "Free" || course.pricee === "0" ? (
              currentUser ? (
                <button
                  onClick={handleAddToCart}
                  className="w-full inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded transition-all duration-300"
                >
                  <ShoppingCartIcon className="mr-2 h-4 w-4" />
                  {course.price_status === "Free" || course.pricee === "0" ? "Start Learning" : "Add to Cart"}
                </button>
              ) : (
                <Link
                  to="/signin"
                  className="w-full inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold rounded transition-all duration-300"
                >
                  <ShoppingCartIcon className="mr-2 h-4 w-4" />
                  {course.price_status === "Free" || course.pricee === "0" ? "Start Learning" : "Add to Cart"}
                </Link>
              )
            ) : (
              /* For iOS WebView with paid courses, show a simple "View Details" button */
              <button
                onClick={() => window.location.href = `/courses/${course.id}`}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-bold rounded transition-all duration-300"
              >
                <AcademicCapIcon className="mr-2 h-4 w-4" />
                View Details
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Full Image Modal */}
      {showFullImage && course.image_url && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="relative max-w-4xl max-h-[90vh] w-full h-full">
            <img
              src={course.image_url}
              alt={course.name}
              className="w-full h-full object-contain rounded-lg"
            />
            <button
              onClick={() => setShowFullImage(false)}
              className="absolute top-4 right-4 bg-white/90 hover:bg-white text-gray-700 hover:text-gray-900 p-2 rounded-full shadow-lg transition-all duration-200"
              title="Close"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

FeaturedCourseCard.displayName = 'FeaturedCourseCard';

export default FeaturedCourseCard;
